import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { EditorView, keymap } from '@codemirror/view';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { useThemePreference } from '@/components/ThemePreferenceContext';

/**
 * CodeMirror declares no `cursor` of its own, so `cursor: auto` only yields an I-beam directly over a
 * glyph — the gutter, and the empty space below the last line in a fixed-height editor, fall back to the
 * default arrow and the editor stops looking editable. Scoped to this editor rather than set globally.
 */
const cursorTheme = EditorView.theme({
    '.cm-scroller': { cursor: 'text' },
    '.cm-gutters': { cursor: 'default' },
});

/**
 * js-beautify options for MJML source — the same formatter MJML itself uses on its rendered output, with
 * the two settings that matter here. `inline_custom_elements: false` is the one that makes it work at all:
 * left on, every `mj-*` tag counts as inline text and the formatter is a no-op. Wrapping stays off so it
 * never breaks a line inside prose, where the added newline would render as an extra space.
 */
const beautifyOptions = {
    indent_size: 4,
    inline_custom_elements: false,
    indent_handlebars: true,
    wrap_line_length: 0,
    preserve_newlines: true,
    max_preserve_newlines: 2,
    end_with_newline: false,
};

/** Offset of the start of the given 1-based line, or of the last line when the text has fewer. */
const lineStartOffset = (text: string, line: number) => {
    let offset = 0;
    for (let i = 1; i < line; i++) {
        const next = text.indexOf('\n', offset);
        if (next === -1) {
            return offset;
        }
        offset = next + 1;
    }
    return offset;
};

export interface CodeEditorHandle {
    /** Inserts at the cursor (replacing any selection), leaves the cursor after the text, and focuses. */
    insertText(text: string): void;
    /** Re-indents the whole document as one undo step. Reports its own failures; never rejects. */
    format(): Promise<void>;
}

interface CodeEditorProps {
    value: string;
    onChange?(value: string): void;
    readOnly?: boolean;
    /** CSS height for the editor viewport; it scrolls internally rather than growing the page. */
    height?: string;
    className?: string;
}

/**
 * Source editor for MJML + Handlebars template bodies. MJML is XML-shaped, so the HTML language mode
 * gives usable highlighting and bracket matching; Handlebars expressions are left as plain text inside it.
 */
const CodeEditor = forwardRef<CodeEditorHandle, CodeEditorProps>(
    ({ value, onChange, readOnly = false, height = '480px', className }, ref) => {
        const { themePreference } = useThemePreference();
        const editorRef = useRef<ReactCodeMirrorRef>(null);

        const format = useCallback(async () => {
            const view = editorRef.current?.view;
            if (!view || readOnly) {
                return;
            }
            try {
                // Loaded on first use: the formatter is a bigger download than the editor itself, and most
                // visits to a template never press it.
                const { default: beautifier } = await import('js-beautify/js/lib/beautify-html.js');
                const source = view.state.doc.toString();
                const formatted = beautifier.html_beautify(source, beautifyOptions);
                if (formatted === source) {
                    return;
                }
                // Hold the cursor on its line. Re-indenting keeps lines one-to-one, so this is exact for the
                // common case and merely close for the one where it cannot be (a template all on one line).
                const line = view.state.doc.lineAt(view.state.selection.main.head).number;
                view.dispatch({
                    changes: { from: 0, to: view.state.doc.length, insert: formatted },
                    selection: { anchor: lineStartOffset(formatted, line) },
                    scrollIntoView: true,
                });
            } catch {
                Toast.error('Could not format the template');
            }
        }, [readOnly]);

        useImperativeHandle(
            ref,
            () => ({
                insertText(text: string) {
                    const view = editorRef.current?.view;
                    if (!view || readOnly) {
                        return;
                    }
                    view.dispatch(view.state.replaceSelection(text));
                    view.focus();
                },
                format,
            }),
            [readOnly, format]
        );

        // Templates are long lines of markup, so wrap instead of scrolling sideways.
        const extensions = useMemo(
            () => [
                html(),
                EditorView.lineWrapping,
                cursorTheme,
                // The shortcut VS Code uses for the same thing.
                keymap.of([
                    {
                        key: 'Shift-Alt-f',
                        run: () => {
                            void format();
                            return true;
                        },
                    },
                ]),
            ],
            [format]
        );

        return (
            <CodeMirror
                ref={editorRef}
                className={className}
                value={value}
                height={height}
                theme={themePreference}
                editable={!readOnly}
                readOnly={readOnly}
                extensions={extensions}
                onChange={onChange}
                basicSetup={{ foldGutter: true, highlightActiveLine: !readOnly }}
            />
        );
    }
);

CodeEditor.displayName = 'CodeEditor';

export default CodeEditor;
